import { metaLines } from "./export.js";
import { pageSize } from "./paper.js";
const esc = (v) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
// The colours of paper. Written out because the theme's tokens are not in this document, and they
// would be the wrong ones if they were: the theme follows the screen into dark mode, and a page
// printed from a dark screen is still printed on white. The six tones are the badge tones — ink
// and a tenth of it behind — so a mark reads on paper as it does on screen.
const PAPER = {
    ink: '#111827', // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
    quiet: '#374151', // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
    muted: '#5f6368', // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
    head: '#f1f3f5', // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
    edge: '#9aa0a6', // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
    rule: '#e5e7eb', // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
    ground: '#ffffff', // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
};
const TONE = {
    green: { ink: '#166534', ground: '#dcf5e6' }, // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
    amber: { ink: '#92400e', ground: '#fdf0d5' }, // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
    red: { ink: '#991b1b', ground: '#fbe0e0' }, // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
    blue: { ink: '#1e40af', ground: '#ddebfb' }, // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
    violet: { ink: '#5b21b6', ground: '#ede6fb' }, // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
    neutral: { ink: '#374151', ground: '#eef0f3' }, // design-ok: paper is always light; the theme follows the screen into dark mode and is not in the print frame
};
const NUMERIC = new Set(['number', 'money', 'count']);
/** The whole print document, as HTML. */
export function printDocument(table, meta, ctx, paper) {
    const { dir, locale } = ctx.i18n;
    const lines = metaLines(meta, ctx);
    const head = table.columns.map((c) => `<th data-print-column="${esc(c.key)}"${NUMERIC.has(c.kind) ? ' class="num"' : ''}>${esc(c.label)}</th>`).join('');
    const body = table.rows.map((row) => `<tr>${row.map((cell, i) => {
        const kind = table.columns[i].kind;
        if (cell.tone)
            return `<td><span class="pill" data-tone="${cell.tone}">${esc(cell.text)}</span></td>`;
        // Numbers and days are isolated so a minus sign or a date's dashes are not re-ordered by the
        // Arabic around them; words take the paragraph's direction from their own first letter.
        if (NUMERIC.has(kind))
            return `<td class="num"><bdi>${esc(cell.text)}</bdi></td>`;
        // A day or a clock time broken over two lines reads as two values.
        if (kind === 'date' || kind === 'datetime' || kind === 'time' || table.columns[i].key.endsWith('.hijri'))
            return `<td class="when"><bdi>${esc(cell.text)}</bdi></td>`;
        return `<td><bdi>${esc(cell.text)}</bdi></td>`;
    }).join('')}</tr>`).join('');
    // The totals are the last row of the body, not a footer: a footer repeats on every printed page,
    // and a «total» under page one of five would be the total of something the page does not show.
    const totals = table.totals ? `<tr class="total" data-print-totals>${table.totals.map((cell, i) => {
        const kind = table.columns[i].kind;
        return NUMERIC.has(kind) ? `<td class="num"><bdi>${esc(cell.text)}</bdi></td>` : `<td><bdi>${esc(cell.text)}</bdi></td>`;
    }).join('')}</tr>` : '';
    const tones = Object.keys(TONE).map((t) => `.pill[data-tone="${t}"]{color:${TONE[t].ink};background:${TONE[t].ground}}`).join('');
    return `<!doctype html>
<html lang="${esc(locale)}" dir="${dir}">
<head>
<meta charset="utf-8">
<title>${esc(meta.title)}</title>
<style>
@page{size:${pageSize(paper, table.columns.length)};margin:12mm 10mm 14mm;@bottom-center{content:counter(page) " / " counter(pages);font:8.5pt system-ui,sans-serif;color:${PAPER.muted}}}
*{box-sizing:border-box}
html{-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{margin:0;font:9.5pt/1.45 "IBM Plex Sans Arabic","Segoe UI",Tahoma,"Noto Sans Arabic",Arial,sans-serif;color:${PAPER.ink};background:${PAPER.ground}}
header{margin-bottom:10pt;padding-bottom:8pt;border-bottom:1.5pt solid ${PAPER.ink}}
h1{margin:0 0 6pt;font-size:15pt;line-height:1.3}
dl{display:grid;grid-template-columns:max-content 1fr;gap:2pt 10pt;margin:0;font-size:8.5pt;color:${PAPER.quiet}}
dt{font-weight:600;color:${PAPER.muted}}
dd{margin:0}
table{width:100%;border-collapse:collapse}
thead{display:table-header-group}
tfoot{display:table-footer-group}
tr{break-inside:avoid;page-break-inside:avoid}
th{background:${PAPER.head};font-weight:700;text-align:start;vertical-align:bottom;padding:4pt 5pt;border-bottom:1pt solid ${PAPER.edge};font-size:8.5pt}
td{padding:3pt 5pt;border-bottom:.5pt solid ${PAPER.rule};vertical-align:top}
.num{text-align:end;font-variant-numeric:tabular-nums;white-space:nowrap}
.when{white-space:nowrap;font-variant-numeric:tabular-nums}
.pill{display:inline-block;padding:0 6pt;border-radius:8pt;font-weight:600;font-size:8.5pt;white-space:nowrap}
${tones}
.empty{padding:14pt;text-align:center;color:${PAPER.muted}}
.total td{font-weight:700;border-top:1pt solid ${PAPER.edge};border-bottom:none}
</style>
</head>
<body>
<header data-print-head>
<h1>${esc(meta.title)}</h1>
<dl>${lines.map(([label, value]) => `<dt>${esc(label)}</dt><dd><bdi>${esc(value)}</bdi></dd>`).join('')}</dl>
</header>
<table data-print-table>
<thead><tr>${head}</tr></thead>
<tbody>${body || `<tr><td class="empty" colspan="${Math.max(1, table.columns.length)}">${esc(ctx.i18n.t('list.empty.title'))}</td></tr>`}${totals}</tbody>
</table>
</body>
</html>`;
}
/**
 * Print the document from a frame of its own, off screen, and take the frame away afterwards.
 *
 * A frame and not a new window: on a paged source the rows arrive after a request, and a window
 * opened after an await is a popup the browser blocks. A frame is part of this page, so its print
 * dialog opens whenever it is asked, and it previews exactly what will be saved.
 */
export function openPrintView(html) {
    if (typeof document === 'undefined')
        return;
    const frame = document.createElement('iframe');
    frame.setAttribute('data-print-frame', '');
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    // Off screen, but laid out and visible at a page's width: a frame of zero size, or one hidden,
    // prints blank in some engines.
    frame.style.cssText = 'position:fixed;left:-10000px;top:0;width:1100px;height:800px;border:0';
    let done = false;
    const remove = () => { if (!done) {
        done = true;
        frame.remove();
    } };
    frame.addEventListener('load', () => {
        const win = frame.contentWindow;
        if (!win) {
            remove();
            return;
        }
        win.addEventListener('afterprint', () => setTimeout(remove, 0));
        // A print dialog nobody closes should not leave a frame behind for the life of the tab.
        setTimeout(remove, 10 * 60_000);
        win.focus();
        win.print();
    }, { once: true });
    frame.srcdoc = html;
    document.body.appendChild(frame);
}
//# sourceMappingURL=export-print.js.map